import { DEFAULT_ADMIN_FILTER_STATE } from '../components/articleSearchConfigs';
import { filterStateToParams } from './searchState';

interface LinkWrapperStory {
  canonical_domain?: string | null;
  linked_url?: string | null;
}

const LINK_WRAPPER_DOMAINS = new Set([
  'news.ycombinator.com',
  'x.com',
  'twitter.com',
  'mobile.twitter.com',
  'nitter.net',
]);

export function domainSearchUrl(domain: string): string {
  const params = filterStateToParams({
    filters: [{ id: 'domain-link', field: 'domain', operator: 'equals', value: domain }],
    orderBy: 'created_at',
    orderDir: 'desc',
  }, DEFAULT_ADMIN_FILTER_STATE);
  return `/search?${params.toString()}`;
}

export function isLinkWrapperStory(story: LinkWrapperStory): boolean {
  return Boolean(
    story.linked_url
    && story.canonical_domain
    && LINK_WRAPPER_DOMAINS.has(story.canonical_domain),
  );
}

/**
 * The URL a reader-facing story link should open.
 *
 * An aggregator wrapper row carries two URLs: `url` is the aggregator
 * submission (for hnrss rows that HN permalink is the durable row identity —
 * see `extract_entry_urls` in backend/app/services/feed_url_helpers.py) and
 * `linked_url` is the article the submission points at. Everything the reader
 * sees on such a row — title, summary, and the host rendered by `linkHost` —
 * describes the *linked article*, so a click has to land there too; sending it
 * to the HN comments page contradicts the row the reader just read.
 *
 * This is the single owner of that resolution. Reader surfaces (StoryRow's
 * title, StoryDetail's "open original") go through it; admin surfaces
 * deliberately keep linking to `story.url`, because they inspect the row
 * itself rather than following it through.
 *
 * Scoped to `isLinkWrapperStory` — i.e. the aggregator domains where the
 * wrapper adds no reader-facing content of its own. Link blogs outside that
 * set (Daring Fireball and friends) carry their own commentary at `url` and
 * are deliberately left alone; widening the behavior means adding a domain to
 * `LINK_WRAPPER_DOMAINS`, not special-casing here.
 */
export function readerLinkUrl<T extends LinkWrapperStory & { url?: string | null }>(
  story: T,
): string | null | undefined {
  return isLinkWrapperStory(story) ? story.linked_url : story.url;
}
